import React, { useState, useEffect } from 'react';
import { Loader2, Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const ServicesPanel = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const { getAuthHeaders } = useAuth();
  
  const emptyForm = {
    name: '',
    slug: '',
    icon: 'Code',
    description: '',
    image: '',
    features: [],
    order: 0
  };
  
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/services`);
      const data = await response.json();
      setServices(data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const url = editingId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/cms/services/${editingId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/cms/services`;
      
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to save');

      toast.success(editingId ? 'Service updated!' : 'Service added!');
      loadServices();
      setFormData(emptyForm);
      setEditingId(null);
      setIsAdding(false);
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;

    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/services/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      toast.success('Service deleted!');
      loadServices();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={48} /></div>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Services</CardTitle>
          {!isAdding && !editingId && (
            <Button onClick={() => { setFormData(emptyForm); setIsAdding(true); }} className="bg-[#320547]">
              <Plus className="mr-2" size={20} /> Add Service
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {(isAdding || editingId) && (
          <div className="mb-6 p-6 border rounded-2xl bg-white">
            <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit' : 'Add'} Service</h3>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name *</label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Slug *</label>
                  <Input value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} required />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description *</label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Image URL *</label>
                <Input value={formData.image} onChange={(e) => setFormData({ ...formData, image: e.target.value })} />
              </div>
              <div className="flex gap-2 justify-end">
                <Button onClick={() => { setFormData(emptyForm); setEditingId(null); setIsAdding(false); }} variant="outline"><X className="mr-2" size={20} />Cancel</Button>
                <Button onClick={handleSave} className="bg-[#320547]"><Save className="mr-2" size={20} />Save</Button>
              </div>
            </div>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4">
          {services.map((service) => (
            <Card key={service.id}>
              <CardContent className="p-4">
                <h4 className="font-bold text-lg">{service.name}</h4>
                <p className="text-sm text-gray-400 mt-1 line-clamp-2">{service.description}</p>
                <div className="flex gap-2 mt-4">
                  <Button onClick={() => { setFormData(service); setEditingId(service.id); setIsAdding(false); }} size="sm" variant="outline" className="flex-1"><Edit2 size={16} className="mr-1" />Edit</Button>
                  <Button onClick={() => handleDelete(service.id)} size="sm" variant="outline" className="text-red-600"><Trash2 size={16} /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        {services.length === 0 && !isAdding && <div className="text-center py-12 text-gray-500">No services yet.</div>}
      </CardContent>
    </Card>
  );
};

export default ServicesPanel;